import { useMemo } from "react";
import { Link } from "react-router";
import { useLingui } from "@lingui/react";
import { ArrowRightIcon, StarIcon } from "lucide-react";
import { StatGrid } from "@/components/ui/stat-card";
import { MetricCard } from "@/components/cockpit/metrics/MetricCard";
import { ANALYTICS_PATH, analyticsMetricHref } from "@/components/cockpit/metrics/metricLinks";
import { useMetricFavourites } from "@/lib/metrics/favourites";
import { getMetric } from "@/lib/metrics/registry";
import type { MetricDefinition } from "@/lib/metrics/types";

interface MyMetricsBlockProps {
  /** The twelve months the cards show, `YYYY-MM`. */
  from: string;
  to: string;
  childcareId?: string | null;
  groupId?: string | null;
  currency: string;
}

/**
 * "My metrics" on the Dashboard (#2175 C6, the phone's MyMetricsBlock): the
 * metrics starred in the Kennzahlen hub as compact cards with their sparkline,
 * each opening its detail on the Analytics page with the Dashboard's scope.
 * Same requests as the hub, so both show one number.
 */
export function MyMetricsBlock({ from, to, childcareId, groupId, currency }: MyMetricsBlockProps) {
  const { i18n } = useLingui();
  const favourites = useMetricFavourites();
  const defs = useMemo(
    () => favourites.ids.map((id) => getMetric(id)).filter((d): d is MetricDefinition => !!d),
    [favourites.ids],
  );
  const scope = useMemo(
    () => ({ childcare_id: childcareId || undefined, group_id: groupId || undefined }),
    [childcareId, groupId],
  );

  return (
    <section data-testid="dashboard-my-metrics" aria-labelledby="dashboard-my-metrics-title" className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="dashboard-my-metrics-title" className="text-base font-semibold">
          {i18n._({ id: "analytics.metrics.favourites", message: "My metrics" })}
        </h2>
        <Link
          to={ANALYTICS_PATH}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {i18n._({ id: "analytics.metrics.back", message: "All metrics" })}
          <ArrowRightIcon aria-hidden="true" className="size-4" />
        </Link>
      </div>
      {defs.length === 0 ? (
        <p className="flex items-center gap-2 rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="dashboard-my-metrics-empty">
          <StarIcon aria-hidden="true" className="size-4 shrink-0" />
          {i18n._({
            id: "dashboard.myMetrics.empty",
            message: "Star a metric under Analytics to see it here.",
          })}
        </p>
      ) : (
        <StatGrid variant="four">
          {defs.map((def) => (
            <MetricCard
              key={def.id}
              def={def}
              from={from}
              to={to}
              scope={scope}
              currency={currency}
              isFavourite
              onToggleFavourite={() => favourites.toggle(def.id)}
              href={analyticsMetricHref(def.id, { childcare: childcareId, group: groupId })}
              compact
              testId={`dashboard-metric-${def.id}`}
            />
          ))}
        </StatGrid>
      )}
    </section>
  );
}
